import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducers';
import { getSelectedFiscalYear } from 'app/internal/property/property-store/property.selectors';
import { getWorkspaceSettings } from 'app/internal/workspace/workspace-store/workspace.selectors';
import { combineLatest, firstValueFrom, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, take, timeout } from 'rxjs/operators';
import { CLUB_SUBSCRIPTION_PLAN_ID } from '../constants/club-subscription-plan-id';

@Injectable({ providedIn: 'root' })
export class MembershipAccessGuard implements CanActivate {
  private static readonly HYDRATION_TIMEOUT_MS = 15000;

  constructor(
    private readonly store: Store<AppState>,
    private readonly router: Router,
    private readonly destroyRef: DestroyRef,
  ) {
    // If eligibility changes while user is already on membership routes (e.g., fiscal year switched to past),
    // enforce the rule by redirecting out.
    combineLatest([this.store.select(getWorkspaceSettings), this.store.select(getSelectedFiscalYear)])
      .pipe(
        filter(([ws, fy]) => !!ws && !!fy),
        map(([ws, fy]) => {
          const planId = ws?.subscriptionPlanId != null ? ws.subscriptionPlanId : 1;
          const isClub = planId === CLUB_SUBSCRIPTION_PLAN_ID;
          const fyOk = !!(fy?.isCurrentFiscalYear || fy?.isFutureFiscalYear);
          return isClub && fyOk;
        }),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(isAllowed => {
        const url = this.router.url ?? '';
        if (!isAllowed && url.includes('/useraccount/membership')) {
          void this.router.navigate(['/useraccount/properties/dashboard']);
        }
      });
  }

  async canActivate(): Promise<boolean | UrlTree> {
    const [ws, fiscalYear] = await firstValueFrom(
      combineLatest([
        this.store.select(getWorkspaceSettings),
        this.store.select(getSelectedFiscalYear),
      ]).pipe(
        // Wait for store hydration (initial emissions can be null/defaults).
        filter(([workspaceSettings, selectedFiscalYear]) => !!workspaceSettings && !!selectedFiscalYear),
        take(1),
        timeout(MembershipAccessGuard.HYDRATION_TIMEOUT_MS),
        // Fail closed: if hydration doesn't happen, avoid hanging navigation forever.
        catchError(() => of([null, null] as const)),
      ),
    );

    if (!ws || !fiscalYear) {
      return this.router.createUrlTree(['/useraccount/properties/dashboard']);
    }

    const planId = ws?.subscriptionPlanId != null ? ws.subscriptionPlanId : 1;
    const isClub = planId === CLUB_SUBSCRIPTION_PLAN_ID;
    const fyOk = !!(fiscalYear?.isCurrentFiscalYear || fiscalYear?.isFutureFiscalYear);

    if (isClub && fyOk) {
      return true;
    }

    return this.router.createUrlTree(['/useraccount/properties/dashboard']);
  }
}

